import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { useFavorites } from "../auth/FavoritesContext";
import { videoCategories, categoryKey, HIDDEN_CATALOG_CATEGORIES } from "../categories";
import Shell from "../components/Shell";
import { buildSeriesShows, showToCatalogItem } from "../series";

const BATCH = 24;

const VARIANTS = {
  movie: {
    // Compat: "anime" residual = película anime hasta migrar.
    types: new Set(["movie", "anime"]),
    basePath: "/peliculas",
    homePath: "/peliculas",
    backLabel: "Películas",
    allTitle: "Películas",
    collapseSeries: false,
    showAllCatalog: false,
  },
  series: {
    types: new Set(["series"]),
    basePath: "/series",
    homePath: "/",
    backLabel: "Inicio",
    allTitle: "Series",
    collapseSeries: true,
    showAllCatalog: true,
  },
  anime: {
    types: new Set(["anime_series"]),
    basePath: "/anime",
    homePath: "/",
    backLabel: "Inicio",
    allTitle: "Anime",
    collapseSeries: true,
    showAllCatalog: true,
  },
};

export function slugifyGenre(name) {
  return (
    String(name || "sin-categoria")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .slice(0, 80) || "sin-categoria"
  );
}

function MovieCard({ video, index = 0 }) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [favBusy, setFavBusy] = useState(false);
  const favored = isFavorite(video.id) || Boolean(video.is_favorite);
  const rating = video.rating_avg;
  const href = video.catalog_href || `/watch/${video.id}`;

  async function onFavoriteClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (favBusy) return;
    setFavBusy(true);
    try {
      await toggleFavorite(video.id);
    } catch (_) {
      /* ignore */
    } finally {
      setFavBusy(false);
    }
  }

  return (
    <article
      className="movies-grid-card"
      style={{ "--reveal-i": String(index % 12) }}
    >
      <Link to={href} className="movies-grid-poster">
        {video.cover_url ? (
          <img src={video.cover_url} alt="" loading="lazy" decoding="async" />
        ) : (
          <div className="poster-fallback">{(video.title || "?").slice(0, 1)}</div>
        )}
        {rating ? (
          <span className="poster-rating" title={`${rating} / 5`}>
            <span className="poster-rating-star" aria-hidden="true">
              ★
            </span>
            <span className="poster-rating-value">{rating}</span>
          </span>
        ) : null}
        <button
          type="button"
          className={`poster-fav ${favored ? "is-on" : ""}`}
          aria-label={favored ? "Quitar de favoritos" : "Añadir a favoritos"}
          onClick={onFavoriteClick}
        >
          {favored ? "★" : "☆"}
        </button>
      </Link>
      <h3 className="movies-grid-title">
        <Link to={href}>{video.title}</Link>
      </h3>
      <p className="muted movies-grid-meta">
        {[video.year, videoCategories(video).join(" · ")].filter(Boolean).join(" · ")}
      </p>
    </article>
  );
}

/** Página de un solo género (desde “Ver más”) con scroll infinito. */
export default function MoviesPage({ variant = "movie" }) {
  const { genreSlug } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const { token, profile } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(BATCH);
  const sentinelRef = useRef(null);
  const cfg = VARIANTS[variant] || VARIANTS.movie;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api("/api/videos", { token })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.videos || [];
        setVideos(list.filter((v) => cfg.types.has(v.content_type || "movie")));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "No se pudo cargar el catálogo");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, profile?.id, variant]);

  const catalogItems = useMemo(() => {
    if (!cfg.collapseSeries) return videos;
    const includeAnime = variant === "anime";
    const shows = buildSeriesShows(videos, { includeAnime }).filter((show) => {
      if (variant === "series") return (show.content_type || "") !== "anime_series";
      if (variant === "anime") return (show.content_type || "") === "anime_series";
      return true;
    });
    return shows.map(showToCatalogItem);
  }, [videos, cfg.collapseSeries, variant]);

  const genres = useMemo(() => {
    const map = new Map();
    catalogItems.forEach((v) => {
      videoCategories(v).forEach((name) => {
        if (HIDDEN_CATALOG_CATEGORIES.has(categoryKey(name))) return;
        if (!map.has(name)) map.set(name, []);
        const bucket = map.get(name);
        const id = v.catalog_key || v.id;
        if (!bucket.some((x) => (x.catalog_key || x.id) === id)) bucket.push(v);
      });
    });
    return [...map.entries()].map(([name, items]) => ({
      name,
      slug: slugifyGenre(name),
      items,
      count: items.length,
    }));
  }, [catalogItems]);

  function sortAndFilterItems(items, term) {
    let list = [...items].sort(
      (a, b) =>
        (b.id || 0) - (a.id || 0) ||
        String(a.title).localeCompare(String(b.title), "es")
    );
    if (term) {
      list = list.filter((v) => {
        const hay = [v.title, v.series_title, ...videoCategories(v)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(term);
      });
    }
    return list;
  }

  function pickBanner(items, fallbackItems = items) {
    return (
      items.find((v) => v.backdrop_url)?.backdrop_url ||
      items.find((v) => v.cover_url)?.cover_url ||
      fallbackItems.find((v) => v.backdrop_url)?.backdrop_url ||
      fallbackItems.find((v) => v.cover_url)?.cover_url ||
      null
    );
  }

  const active = useMemo(() => {
    const term = q.trim().toLowerCase();

    if (!genreSlug && cfg.showAllCatalog) {
      const items = sortAndFilterItems(catalogItems, term);
      return {
        name: cfg.allTitle || cfg.backLabel,
        slug: "all",
        items,
        count: items.length,
        banner: pickBanner(items, catalogItems),
      };
    }

    if (!genreSlug) return null;

    const found = genres.find((g) => g.slug === genreSlug);
    if (!found) return null;

    const items = sortAndFilterItems(found.items, term);
    return {
      name: found.name,
      slug: found.slug,
      items,
      count: items.length,
      banner: pickBanner(items, found.items),
    };
  }, [genres, genreSlug, q, catalogItems, cfg]);

  useEffect(() => {
    setVisible(BATCH);
  }, [genreSlug, q]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !active) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setVisible((n) => {
          if (n >= (active?.count || 0)) return n;
          return Math.min(n + BATCH, active.count);
        });
      },
      { rootMargin: "320px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [active?.slug, active?.count, q]);

  const pageItems = useMemo(() => {
    if (!active) return [];
    return active.items.slice(0, visible);
  }, [active, visible]);

  if (!genreSlug && !cfg.showAllCatalog) {
    return <Navigate to={cfg.basePath} replace />;
  }

  if (!loading && genreSlug && catalogItems.length && !active && !q) {
    const exists = genres.some((g) => g.slug === genreSlug);
    if (!exists) return <Navigate to={cfg.basePath} replace />;
  }

  if (
    !loading &&
    genreSlug &&
    catalogItems.length &&
    !genres.some((g) => g.slug === genreSlug)
  ) {
    return <Navigate to={cfg.basePath} replace />;
  }

  const backHref =
    genreSlug && cfg.showAllCatalog ? cfg.basePath : cfg.homePath || cfg.basePath;
  const backText =
    genreSlug && cfg.showAllCatalog ? `← ${cfg.allTitle || cfg.backLabel}` : `← ${cfg.backLabel}`;

  return (
    <Shell wide>
      <div className="movies-page">
        {active ? (
          <section
            className="movies-genre-hero"
            style={
              active.banner ? { "--movies-hero-bg": `url(${active.banner})` } : undefined
            }
          >
            <div className="movies-genre-hero-bg" aria-hidden="true" />
            <div className="movies-genre-hero-fade" aria-hidden="true" />
            <div className="movies-genre-hero-inner">
              <p className="saga-kicker">
                <Link to={backHref} className="movies-back-link">
                  {backText}
                </Link>
              </p>
              <h1>{active.name}</h1>
              <p className="muted">
                {active.count} título{active.count === 1 ? "" : "s"}
                {q.trim() ? ` · búsqueda “${q.trim()}”` : ""}
              </p>
            </div>
          </section>
        ) : (
          <section className="movies-genre-hero movies-genre-hero-empty">
            <div className="movies-genre-hero-inner">
              <p className="saga-kicker">
                <Link to={backHref} className="movies-back-link">
                  {backText}
                </Link>
              </p>
              <h1>{loading ? "Cargando…" : "Sin resultados"}</h1>
            </div>
          </section>
        )}

        {loading ? <p className="muted catalog-status">Cargando…</p> : null}
        {error ? <p className="error catalog-status">{error}</p> : null}
        {!loading && active && !active.count ? (
          <p className="muted catalog-status">No hay títulos con ese filtro.</p>
        ) : null}

        {active && active.count ? (
          <section
            className={`movies-genre-section ${q.trim() ? "is-searching" : ""}`}
            aria-label={active.name}
            key={`grid-${genreSlug || "all"}-${q}`}
          >
            <div className="movies-grid search-soft">
              {pageItems.map((video, index) => (
                <MovieCard
                  key={video.catalog_key || video.id}
                  video={video}
                  index={index}
                />
              ))}
            </div>
            {visible < active.count ? (
              <div ref={sentinelRef} className="movies-infinite-sentinel" aria-hidden="true">
                <p className="muted">Cargando más…</p>
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    </Shell>
  );
}
